import {
	Component,
	ElementRef,
	OnDestroy,
	OnInit,
	ViewChild
} from '@angular/core';
import { Location } from '@angular/common';
import { Subject } from 'rxjs/Subject';
import { debounceTime, finalize, map, takeUntil, tap } from 'rxjs/operators';
import { MessageService } from '@services/helpers/message-service/message.service';
import { AbstractControl, FormBuilder, FormGroup } from '@angular/forms';
import { InvoicePaymentsDataService } from '@web-services/settings/invoice-payments/invoice-payments.data';
import {
	DateFormatMeta,
	InvoiceNumberingPayload,
	InvoiceNumberingRules
} from '@models/settings.model';
import { Observable } from 'rxjs';
import { combineLatest } from 'rxjs/observable/combineLatest';

@Component({
	selector: 'invoice-numbering',
	template: require('./invoice-numbering.component.html'),
	styles: [require('./invoice-numbering.component.scss')]
})
export class InvoiceNumberingComponent implements OnInit, OnDestroy {
	@ViewChild('invoiceID') invoiceID: ElementRef;

	invoiceIdData: InvoiceNumberingRules;
	invoiceIdForm: FormGroup;
	buttonDisable: boolean;
	dateFormat: DateFormatMeta;
	generatedID: string;
	private unsubscribe: Subject<void> = new Subject();

	constructor(
		private invoicePaymentsDataService: InvoicePaymentsDataService,
		private messageService: MessageService,
		private fb: FormBuilder,
		public location: Location
	) {}

	ngOnInit(): void {
		this.getInvoiceNumberingData();
	}

	ngOnDestroy(): void {
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}

	saveInvoiceIdFormat(): void {
		if (this.isFormValid()) {
			const data = this.prepareDataToSend();
			this.saveInvoiceRules(data);
		}
	}

	private saveInvoiceRules(data: InvoiceNumberingPayload): void {
		this.buttonDisable = true;
		this.invoicePaymentsDataService
			.saveInvoiceNumberingRules(data)
			.pipe(
				takeUntil(this.unsubscribe),
				finalize((): boolean => (this.buttonDisable = false))
			)
			.subscribe(
				(): void => {
					this.messageService.showToaster('s_5', 'paymentSchedules');
				},
				(err): void => this.handleError(err)
			);
	}

	private getInvoiceNumberingData(): void {
		this.invoicePaymentsDataService
			.getInvoiceNumberingRules()
			.pipe(takeUntil(this.unsubscribe))
			.subscribe(
				({ data }): void => {
					this.invoiceIdData = data;
					this.createForm(data);

					const [key] = Object.keys(data.metadata.dateFormats);
					this.dateFormat = data.metadata.dateFormats[key];
					this.scrollIntoView('invoiceID', 'redirectTo=InvoiceID');
				},
				(err): void => this.handleError(err)
			);
	}

	private createForm(data: InvoiceNumberingRules): void {
		if (data.type === 'DATE') {
			const {
				defaultNumberOfDigits,
				minStartsFrom
			} = this.invoiceIdData.metadata.types.LETTERS_AND_NUMBERS;
			data.numberOfDigits = defaultNumberOfDigits;
			data.startsFrom = minStartsFrom;
		}

		this.invoiceIdForm = this.fb.group({
			type: [data.type],
			sequentialConfig: this.fb.group({
				letters: [data.letters],
				numberOfDigits: [data.numberOfDigits],
				startsFrom: [data.startsFrom]
			})
		});
		this.createIdPreview();
		this.setDynamicValidators();
	}

	private prepareDataToSend(): InvoiceNumberingPayload {
		const { type, sequentialConfig } = this.invoiceIdForm.value;
		if (type === 'DATE') {
			const { defaultNumberOfDigits } = this.invoiceIdData.metadata.types.DATE;
			return {
				type,
				numberOfDigits: defaultNumberOfDigits,
				dateFormat: this.dateFormat.name
			};
		}

		if (type === 'LETTERS_AND_NUMBERS') {
			const { letters, numberOfDigits, startsFrom } = sequentialConfig;
			return {
				type,
				letters,
				numberOfDigits,
				startsFrom
			};
		}
	}

	private isFormValid(): boolean {
		const { sequentialConfig, type } = this.invoiceIdForm.value;

		if (type === 'LETTERS_AND_NUMBERS') {
			const { letters } = sequentialConfig;

			const regex = /^[A-z0-9\u00C0-\u00ff\s-\/\-_\\]+$/;
			if (letters && !regex.test(letters)) {
				this.messageService.showToaster('e_5', 'paymentSchedules');
				return false;
			}
		}

		return true;
	}

	private generateNumberPortion({ numberOfDigits, startsFrom }): void {
		startsFrom = `${startsFrom || 0}`;
		return startsFrom.length >= numberOfDigits
			? startsFrom
			: new Array(numberOfDigits - startsFrom.length + 1).join('0') +
					startsFrom;
	}

	private handleError({ error }): void {
		const { errorTitle, errorMessage } = error;
		this.messageService.showErrorFromBackend(errorTitle, errorMessage);
	}

	private createIdPreview(): void {
		const { sequentialConfig } = this.invoiceIdForm.value;

		this.generatedID = `${sequentialConfig.letters}${this.generateNumberPortion(
			sequentialConfig
		)}`;
	}

	private setDynamicValidators(): void {
		const lettersGroup: AbstractControl = this.invoiceIdForm.get(
			'sequentialConfig'
		);
		combineLatest(
			this.trimLetters(lettersGroup.get('letters')),
			this.validateNumberField(lettersGroup.get('numberOfDigits'), {
				min: 1,
				max: 6,
				numbersAfterPoint: 0
			}),
			this.validateNumberField(lettersGroup.get('startsFrom'), {
				min: 1,
				max: 100000,
				numbersAfterPoint: 0
			})
		)
			.pipe(takeUntil(this.unsubscribe))
			.subscribe();
	}

	private trimLetters(control: AbstractControl): Observable<string | void> {
		return control.valueChanges.pipe(
			debounceTime(200),
			map((value: string): string => value.replace(/\s/g, '')),
			map((value: string): void => control.patchValue(value)),
			tap((): void => this.createIdPreview())
		);
	}

	private validateNumberField(
		control: AbstractControl,
		config
	): Observable<number> {
		return control.valueChanges.pipe(
			debounceTime(200),
			tap((value: number): void => {
				let newValue = value;

				if (newValue) {
					const [, decimal] = newValue.toString().split('.');
					if (decimal && decimal.length > config.numbersAfterPoint) {
						newValue = Math.trunc(newValue);
					}
				}

				if (newValue < config.min) {
					newValue = config.min;
				}

				if (newValue > config.max) {
					newValue = config.max;
				}

				control.patchValue(newValue);
				this.createIdPreview();
			})
		);
	}

	private scrollIntoView(scrollDest: string, checkPath: string): void {
		if (this.location.path().includes(checkPath)) {
			requestAnimationFrame((): void => {
				this[scrollDest].nativeElement.scrollIntoView();
			});
		}
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=oom9nsliule!./web/modern/components/settings/invoice-payments-section/components/invoice-numbering/invoice-numbering.component.ts